import React from 'react';
import { Link } from 'react-router-dom';
import { AppShot } from '../components/AppShot';

const groups = [
  {
    title: 'Work items',
    intro: 'The unit of work. A workspace holds them, and you can look at the same set five ways.',
    items: [
      {
        title: 'Board, list, timeline, calendar, workload',
        text: 'Switch views without leaving the workspace. The board is drag and drop across New, In Progress, In Review, Blocked, Completed, and Cancelled.',
      },
      {
        title: 'Search, status, and priority',
        text: 'Filter the workspace by status and priority, or search the title. Home also has chips for your tasks, high priority, due this week, overdue, and unassigned.',
      },
      {
        title: 'Comments, tags, and files',
        text: 'Discussion stays on the item. Tags come from your dimensions. Files attach to the work.',
      },
      {
        title: 'CSV in and out',
        text: 'Import and export work items from the workspace when you are moving a list, not retyping it.',
      },
    ],
  },
  {
    title: 'How work is classified',
    intro: 'Dimensions are the organizing idea. A dimension is a category you define. Tags live inside it.',
    items: [
      {
        title: 'Dimensions and tags',
        text: 'The demo has Business Units (5 tags), Category (4), and Priority (3). An item can carry tags from more than one dimension, which is how “Insurance” and “IT Cell” sit on the same card.',
      },
      {
        title: 'Roles',
        text: 'Admin, Manager, Contributor, Stakeholder, and Guest. A guest can view and comment on assigned workspaces. Stakeholders get read-oriented access, including reports.',
      },
      {
        title: 'Pages and share links',
        text: 'Each workspace has a page tree for the write-up that should not live in a comment. Share links let a client open a page without an account.',
      },
    ],
  },
  {
    title: 'Time, money, and the rollup',
    intro: 'A workspace can carry a budget. The organization can see every workspace at once.',
    items: [
      {
        title: 'Timer, entries, reports',
        text: 'Start a timer against a work item, mark it billable, or add an entry by hand. Reports sit on the same Time page.',
      },
      {
        title: 'Budget and utilization',
        text: 'Set a budget, then read billable burn, remaining, and hours against the estimate. The panel also splits estimated hours by assignee.',
      },
      {
        title: 'Portfolio',
        text: 'Open items, overdue, at-risk workspaces, estimated hours, and budget remaining. The weekly email goes out Mondays at 07:00 UTC.',
      },
      {
        title: 'Analytics',
        text: 'Per workspace: a dashboard, team performance, and time tracking. Pick the workspace, then update the snapshot.',
      },
    ],
  },
  {
    title: 'Automation, assistant, and the API',
    intro: 'Rules for the repetitive cases. An assistant for the question you would otherwise ask in a standup. Keys for the tools you already run.',
    items: [
      {
        title: 'Rules',
        text: 'Eight triggers: created, updated, status, assignment, comment, due date approaching, tag added, priority. Nine actions, including assign, change status or priority, add or remove a tag, notify, create an item, comment, and set a due date.',
      },
      {
        title: 'Templates',
        text: 'Thirty-seven templates across project management, development, support, sales, and general. “Blocked work escalates” notifies the workspace the moment something lands in Blocked.',
      },
      {
        title: 'Assistant',
        text: 'Scoped to a workspace. It can list items, apply tags from your dimensions, and propose changes. Destructive actions wait on an approval card. Session artifacts collect the items and pages it touched.',
      },
      {
        title: 'Developer',
        text: 'API keys and outbound webhooks aimed at Zapier, Make, n8n, and your own code. The organization is taken from the key.',
      },
    ],
  },
];

export const Features: React.FC = () => {
  return (
    <div className="pb-20 pt-28">
      <div className="container-width section-padding">
        <p className="eyebrow">Features</p>
        <h1 className="max-w-3xl font-display text-4xl sm:text-5xl">What is in the product today.</h1>
        <p className="lede mt-5">
          This list follows the app, not an older brochure. Screens are from the demo organization.
          There is no built-in video calling, no native mobile app, and no Slack or Google connector
          inside the product. The API and webhooks are how those connections get made.
        </p>

        <div className="mt-12 grid gap-4 sm:grid-cols-2">
          <AppShot
            src="/images/app/workspaces.jpg"
            alt="Workspace cards for Acme Insurance, FleetLink Transport, Northstar Travel, and Demo"
            caption="Workspaces. Each card is one environment, with an owner."
          />
          <AppShot
            src="/images/app/dimensions.jpg"
            alt="Dimensions page listing Business Units, Category, and Priority"
            caption="Dimensions in the demo organization."
          />
        </div>

        <div className="mt-16 space-y-16">
          {groups.map((group) => (
            <section key={group.title}>
              <h2 className="font-display text-3xl">{group.title}</h2>
              <p className="mt-3 max-w-2xl text-ink-soft">{group.intro}</p>
              <div className="mt-6 grid gap-4 md:grid-cols-2">
                {group.items.map((item) => (
                  <article key={item.title} className="surface-card p-5">
                    <h3 className="font-display text-xl">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink-soft">{item.text}</p>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 grid gap-4 lg:grid-cols-2">
          <AppShot
            src="/images/app/automation.jpg"
            alt="Automation template library with featured rules for triage, blocked work, and notifications"
            caption="The template library. The demo organization had no custom rules turned on when this was taken."
          />
          <AppShot
            src="/images/app/time.jpg"
            alt="Time page with a timer, a work item picker, a billable switch, and an empty recent entries list"
            caption="Time. The timer needs a work item. This demo account had no entries yet."
          />
        </div>

        <p className="mt-12 text-ink-soft">
          Plans and the limits that come with them are on the <Link className="font-semibold text-forest" to="/pricing">pricing page</Link>.
          A walk through the screens is on the <Link className="font-semibold text-forest" to="/product-tour">product tour</Link>.
        </p>
      </div>
    </div>
  );
};
